import React, { useState, useEffect } from "react";
import { Modal, Table } from "react-bootstrap";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { getPromoCodeUsageStatsAsync } from "../../redux/promoApi";
import CircularProgress from "@material-ui/core/CircularProgress";

// Custom styles for enhanced UI
const customStyles = {
    modal: {
        borderRadius: '15px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    },
    card: {
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
    },
    cardHover: {
        transform: 'translateY(-5px)',
        boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
    },
    progressBar: {
        borderRadius: '10px',
        overflow: 'hidden'
    },
    tableRow: {
        transition: 'all 0.2s ease'
    },
    tableRowHover: {
        backgroundColor: '#f8f9fa',
        transform: 'scale(1.01)'
    }
};

const PromoCodeUsageModal = ({ showUsageModal, setShowUsageModal, selectedPromo }) => {
    const [usageData, setUsageData] = useState(null);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);

    const dispatch = useDispatch();

    // Get usage data from Redux state
    const { isUsageLoading, usageData: reduxUsageData } = useSelector(
        (state) => state.promoCodeManagement,
        shallowEqual
    );

    const onHideModal = () => {
        setShowUsageModal(false);
        setUsageData(null);
    };

    useEffect(() => {
        if (showUsageModal && selectedPromo?._id) {
            fetchUsageData();
        }
    }, [showUsageModal, selectedPromo]);

    useEffect(() => {
        // Update local state when Redux state changes
        if (reduxUsageData) {
            setUsageData(reduxUsageData);
        }
    }, [reduxUsageData]);

    const fetchUsageData = async () => {
        try {
            await dispatch(getPromoCodeUsageStatsAsync(selectedPromo._id));
        } catch (error) {
            console.error('Error fetching usage data:', error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Modal 
            show={showUsageModal} 
            onHide={onHideModal} 
            centered 
            size="xl"
            className="def_modal usage_modal" 
            enforceFocus={false}
            style={customStyles.modal}
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fn_s_20 text-white-20  f-w-700 d-flex align-items-center">
                    <span className="mr-2">📊</span>
                    Promo Code Usage Details: <span className="text-white ml-2">{selectedPromo?.name}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                {isUsageLoading ? (
                    <div className="text-center p-5">
                        <CircularProgress size={60} />
                        <p className="mt-3 text-muted">Loading usage data...</p>
                    </div>
                ) : usageData ? (
                    <div>
                        {/* Promo Code Summary Cards */}
                        <div className="row mb-4">
                            <div className="col-md-6 mb-3">
                                <div 
                                    className="card border-0 shadow-sm h-100" 
                                    style={{ 
                                        ...customStyles.card,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        ...(hoveredCard === 'details' && customStyles.cardHover)
                                    }}
                                    onMouseEnter={() => setHoveredCard('details')}
                                    onMouseLeave={() => setHoveredCard(null)}
                                >
                                    <div className="card-body text-white p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="bg-white bg-opacity-20 rounded-circle p-2 mr-3">
                                                <span className="text-white">🎯</span>
                                            </div>
                                            <h5 className="mb-0 f-w-600">Code Details</h5>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-white-50">Code</small>
                                            <div className="h5 mb-0 f-w-700">{usageData.promoCode.code}</div>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-white-50">Free Coins</small>
                                            <div className="h5 mb-0 f-w-700">🪙 {usageData.promoCode.freeCoins}</div>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-white-50">Max Usage</small>
                                            <div className="h5 mb-0 f-w-700">{usageData.promoCode.maxUsage || '∞ Unlimited'}</div>
                                        </div>
                                        <div>
                                            <small className="text-white-50">Total Users</small>
                                            <div className="h5 mb-0 f-w-700">👥 {usageData.promoCode.totalUsersUsed}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">
                                <div 
                                    className="card border-0 shadow-sm h-100" 
                                    style={{ 
                                        ...customStyles.card,
                                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                        ...(hoveredCard === 'stats' && customStyles.cardHover)
                                    }}
                                    onMouseEnter={() => setHoveredCard('stats')}
                                    onMouseLeave={() => setHoveredCard(null)}
                                >
                                    <div className="card-body text-white p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="bg-white bg-opacity-20 rounded-circle p-2 mr-3">
                                                <span className="text-white">📈</span>
                                            </div>
                                            <h5 className="mb-0 f-w-600">Usage Statistics</h5>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-white">Remaining Uses</small>
                                            <div className="h5 mb-0 f-w-700">
                                                {usageData.promoCode.remainingUses === 'unlimited' ? '∞ Unlimited' : usageData.promoCode.remainingUses}
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-white">Usage Percentage</small>
                                            <div className="h5 mb-0 f-w-700">{usageData.promoCode.usagePercentage}%</div>
                                        </div>
                                        <div>
                                            <small className="text-white">Status</small>
                                            <div className="mt-1">
                                                <span className={`badge ${usageData.promoCode.isLimitReached ? 'badge-danger' : 'badge-success'} px-3 py-2`} style={{ fontSize: '0.9rem' }}>
                                                    {usageData.promoCode.isLimitReached ? 'Limit Reached' : 'Available'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {usageData.promoCode.maxUsage && (
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="text-muted">Usage Progress</span>
                                    <span className="text-muted">{usageData.promoCode.totalUsersUsed || 0} / {usageData.promoCode.maxUsage}</span>
                                </div>
                                <div className="progress" style={{ ...customStyles.progressBar, height: '12px' }}>
                                    <div 
                                        className={`progress-bar ${usageData.promoCode.isLimitReached ? 'bg-danger' : 'bg-success'}`}
                                        style={{ 
                                            width: `${usageData.promoCode.usagePercentage}%`,
                                            transition: 'width 0.8s ease, background-color 0.3s ease'
                                        }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {/* Users Table */}
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-light border-0 py-3">
                                <h6 className="mb-0 f-w-600 text-white d-flex align-items-center">
                                    <span className="mr-2">👥</span>
                                    Users Who Used This Code
                                    <span className="badge badge-primary ml-2">{usageData.users?.length || 0}</span>
                                </h6>
                            </div>
                            <div className="card-body p-0">
                                {usageData.users && usageData.users.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table className="mb-0">
                                            <thead className="">
                                                <tr>
                                                    <th className="border-0 py-3 px-4 f-w-600">#</th>
                                                    <th className="border-0 py-3 px-4 f-w-600">User</th>
                                                    <th className="border-0 py-3 px-4 f-w-600">Username</th>
                                                    <th className="border-0 py-3 px-4 f-w-600">Email</th>
                                                    <th className="border-0 py-3 px-4 f-w-600">Free Coins</th>
                                                    <th className="border-0 py-3 px-4 f-w-600">Used At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {usageData.users.map((user, index) => (
                                                    <tr 
                                                        key={user.user_id} 
                                                        className="border-bottom"
                                                        style={{
                                                            ...customStyles.tableRow,
                                                            ...(hoveredRow === index && customStyles.tableRowHover)
                                                        }}
                                                        // onMouseEnter={() => setHoveredRow(index)}
                                                        // onMouseLeave={() => setHoveredRow(null)}
                                                    >
                                                        <td className="py-3 px-4">
                                                            <span className="badge badge-secondary rounded-circle px-2 py-1">
                                                                {index + 1}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <div className="d-flex align-items-center">
                                                                {user.picture ? (
                                                                    <img 
                                                                        src={user.picture} 
                                                                        alt="User" 
                                                                        className="rounded-circle mr-3" 
                                                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <div className="bg-gradient-primary rounded-circle mr-3 d-flex align-items-center justify-content-center" 
                                                                         style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                                                        <span className="text-white font-weight-bold f-w-600">
                                                                            {user.displayName?.charAt(0) || user.userName?.charAt(0) || 'U'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className="badge badge-info px-3 py-2">{user.userName || 'N/A'}</span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className="text-muted">{user.email || 'N/A'}</span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className="badge badge-success px-3 py-2">
                                                                🪙 {user.free_coins_received}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className="text-muted">{formatDate(user.used_at)}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center p-5">
                                        <div className="mb-3">
                                            <span style={{ fontSize: '3rem' }}>📭</span>
                                        </div>
                                        <p className="text-muted mb-0">No users have used this promo code yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center p-5">
                        <div className="mb-3">
                            <span style={{ fontSize: '3rem' }}>📊</span>
                        </div>
                        <p className="text-muted mb-0">No usage data available.</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
              
            </Modal.Footer>
        </Modal>
    );
};

export default PromoCodeUsageModal; 